import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Chart, ChartArea, ChartCategoryAxis, ChartCategoryAxisItem, ChartSeries, ChartSeriesItem, ChartValueAxis, ChartValueAxisItem } from "@progress/kendo-react-charts";
import { useLocalization } from "@progress/kendo-react-intl";
import { Loader } from "@progress/kendo-react-indicators";
import axios from "axios";
import { useEffect, useState } from "react";
import { AccessToken } from "../../config/AccessToken";
import { extractAlarmItems, getAlarmEventTypeName, getAlarmZoneName } from "./dashboardUtils";

type EventTypeCount = { label: string; count: number };
type ZoneCount = { zoneName: string; count: number };

type AlarmsKpiData = {
  total: number;
  eventTypeBadges: EventTypeCount[];
  zoneCounts: ZoneCount[];
};

const EMPTY_DATA: AlarmsKpiData = { total: 0, eventTypeBadges: [], zoneCounts: [] };
const MAX_EVENT_TYPE_BADGES = 4;
const MAX_ZONES = 6;

const kpiLabelStyle = { fontSize: 'var(--fs-small)', fontWeight: 700, color: '#6b7280', letterSpacing: '0.03em', textTransform: 'uppercase' as const };

export const AlarmsKpiCard = () => {
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const [instance, accessTokenRequest] = AccessToken();
  const localizationService = useLocalization();
  const languageId = localStorage.getItem('languageId');

  const [data, setData] = useState<AlarmsKpiData>(EMPTY_DATA);
  const [loading, setLoading] = useState(false);

  const labels = {
    total: localizationService.toLanguageString('dashboard.widget.alarms.total', 'Totali'),
    zoneViolations: localizationService.toLanguageString('dashboard.widget.alarms.zone_violations', 'Violazioni di zona')
  };

  useEffect(() => {
    const fetchAlarms = async () => {
      setLoading(true);

      const commonOperations = async (accessToken: any) => {
        const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

        try {
          const [listResult, countResult] = await Promise.allSettled([
            axios.get(`${SERVER_URL}/alarm/list?light=true`, { headers }),
            axios.get(`${SERVER_URL}/alarm/count`, { headers })
          ]);

          const alarms = listResult.status === 'fulfilled' ? extractAlarmItems(listResult.value?.data) : [];

          const eventTypeCounters = new Map<string, number>();
          const zoneCounters = new Map<string, number>();

          alarms.forEach((alarm) => {
            const eventTypeName = getAlarmEventTypeName(alarm);
            if (eventTypeName) {
              eventTypeCounters.set(eventTypeName, (eventTypeCounters.get(eventTypeName) ?? 0) + 1);
            }

            const zoneName = getAlarmZoneName(alarm);
            if (zoneName) {
              zoneCounters.set(zoneName, (zoneCounters.get(zoneName) ?? 0) + 1);
            }
          });

          const eventTypeBadges = Array.from(eventTypeCounters.entries())
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, MAX_EVENT_TYPE_BADGES);

          const zoneCounts = Array.from(zoneCounters.entries())
            .map(([zoneName, count]) => ({ zoneName, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, MAX_ZONES);

          const countValue = countResult.status === 'fulfilled'
            ? Number(countResult.value?.data?.data?.alarmCount ?? countResult.value?.data?.alarmCount)
            : NaN;

          setData({ total: Number.isFinite(countValue) ? countValue : alarms.length, eventTypeBadges, zoneCounts });
        } catch {
          setData(EMPTY_DATA);
        } finally {
          setLoading(false);
        }
      };

      if (localStorage.getItem('accessToken') != null) {
        await commonOperations(localStorage.getItem('accessToken'));
      } else {
        await instance.initialize();
        await instance.acquireTokenSilent(accessTokenRequest)
          .then((accessTokenResponse: any) => commonOperations(accessTokenResponse.accessToken))
          .catch((error: any) => {
            if (error instanceof InteractionRequiredAuthError) {
              instance.acquireTokenRedirect(accessTokenRequest);
            }
            setLoading(false);
          });
      }
    };

    void fetchAlarms();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Loader size="small" type="infinite-spinner" themeColor="primary" />
      </div>
    );
  }

  const hasZones = data.zoneCounts.length > 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        height: '100%',
        color: '#1f2937',
        justifyContent: hasZones ? 'flex-start' : 'center'
      }}
    >
      <div style={{ display: 'flex', gap: '12px', alignItems: hasZones ? 'flex-start' : 'center', justifyContent: hasZones ? 'flex-start' : 'center' }}>
        <div style={{ flex: '0 0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#dc2626' }}>{data.total}</div>
          <div style={kpiLabelStyle}>{labels.total}</div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: hasZones ? 'flex-start' : 'center' }}>
          {data.eventTypeBadges.map((badge) => (
            <div
              key={badge.label}
              style={{
                background: '#fdecea',
                borderRadius: '10px',
                padding: '6px 10px',
                textAlign: 'center',
                minWidth: '90px'
              }}
            >
              <div style={{ fontSize: 'var(--fs-small)', fontWeight: 700, color: '#9f3d2e', textTransform: 'uppercase' }}>
                {badge.label}
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#dc2626' }}>{badge.count}</div>
            </div>
          ))}
        </div>
      </div>

      {hasZones && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ ...kpiLabelStyle, marginBottom: '4px' }}>{labels.zoneViolations}</div>
          <div style={{ flex: 1, minHeight: '150px' }}>
            <Chart style={{ height: '100%' }} transitions={false}>
              <ChartArea background="transparent" />
              <ChartCategoryAxis>
                <ChartCategoryAxisItem
                  categories={data.zoneCounts.map((zone) => zone.zoneName)}
                  labels={{ font: '0.75rem sans-serif', color: '#4b5563' }}
                  line={{ color: '#d1d5db' }}
                />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem
                  min={0}
                  labels={{ font: '0.75rem sans-serif', color: '#4b5563' }}
                  line={{ color: '#d1d5db' }}
                  majorGridLines={{ color: '#e5e7eb' }}
                />
              </ChartValueAxis>
              <ChartSeries>
                <ChartSeriesItem
                  type="column"
                  data={data.zoneCounts.map((zone) => zone.count)}
                  color="#f59e0b"
                  tooltip={{ visible: true }}
                />
              </ChartSeries>
            </Chart>
          </div>
        </div>
      )}
    </div>
  );
};
